import React from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { extractResponseData, SmartStyles, SmartHeader, SmartSection, NoRecordsCard } from './_SmartFieldRenderer';

/**
 * The payload nests everything under `details` — personal_info, phone_info[],
 * address_info[], email_info[], identity_info — and the generic renderer chips
 * an array-of-objects nested that deep as raw JSON text (see
 * _SmartFieldRenderer's nested-object branch). That is what produced screens
 * full of {"reported_date":"…","type_code":"H","number":"…"}.
 *
 * So this report gets purpose-built sections instead of the generic key list.
 */

// Bureau phone type codes. Only the unambiguous ones are translated; anything
// else falls through to the raw code rather than inventing a label for a
// document someone may act on.
const PHONE_TYPES = { M: 'Mobile', H: 'Home', O: 'Office', W: 'Work', R: 'Residence' };
const phoneType = (c) => PHONE_TYPES[String(c || '').toUpperCase()] || c || '—';

// identity_info values are either a list of {id_number} or an empty object when
// that document was not reported.
const idNumbers = (v) => {
    if (Array.isArray(v)) return v.map((x) => x?.id_number).filter(Boolean);
    if (v && typeof v === 'object') return Object.values(v).map((x) => x?.id_number ?? x).filter((x) => typeof x === 'string' && x);
    return typeof v === 'string' && v ? [v] : [];
};

const ID_LABELS = {
    pan_number: 'PAN',
    aadhaar_number: 'Aadhaar',
    voter_id: 'Voter ID',
    driving_license: 'Driving Licence',
    passport_number: 'Passport',
    ration_card: 'Ration Card',
    other_id: 'Other ID',
};

const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && String(v).trim() !== '' ? n.toLocaleString('en-IN') : v;
};

const Table = ({ rows, cols }) => (
    <DataTable value={rows} size="small" stripedRows showGridlines responsiveLayout="scroll"
        className="p-datatable-sm p-datatable-gridlines mt-2"
        paginator={rows.length > 10} rows={10}>
        {cols.map((c) => (
            <Column key={c.field} field={c.field} header={c.header} body={c.body} style={c.style} />
        ))}
    </DataTable>
);

// `onDownloadPDF`/`pdfPasswordHint` are optional. The shared-link and lead
// history views render this component without them, and those readers have no
// business regenerating a billed report's PDF — so the button only appears when
// the host actually wires it up.
const PrefillByMobileOutputComponent = ({ reportData, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const { responseData, remainingBalance, errorMsg, hasRealData } = extractResponseData(reportData);

    if (!reportData) return <NoRecordsCard subtitle="No data available" />;
    if (!responseData || !hasRealData) {
        return <NoRecordsCard errorMsg={errorMsg} subtitle="No records found for the provided details. Please verify and try again." />;
    }

    const d = responseData;
    const det = d.details || {};
    const pi = det.personal_info || {};

    const personal = {};
    if (d.mobile) personal.mobile = d.mobile;
    if (pi.full_name) personal.full_name = pi.full_name;
    if (pi.dob) personal.date_of_birth = pi.dob;
    if (pi.gender) personal.gender = pi.gender;
    if (pi.age) personal.age = pi.age;
    if (pi.occupation) personal.occupation = pi.occupation;
    if (pi.total_income) personal.total_income = money(pi.total_income);

    const identity = {};
    Object.entries(det.identity_info || {}).forEach(([k, v]) => {
        const nums = idNumbers(v);
        if (nums.length) identity[ID_LABELS[k] || k] = nums.join(', ');
    });

    const phones = (det.phone_info || []).filter(Boolean);
    const addresses = (det.address_info || []).filter(Boolean);
    const emails = (det.email_info || []).filter(Boolean);

    return (
        <div>
            <SmartStyles />
            <SmartHeader title="Mobile Prefill Report" icon="pi-mobile" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />
            <div className="horizontal-report-card">
                {onDownloadPDF && (
                    <>
                        <div className="flex justify-content-end flex-wrap gap-2 mb-2">
                            <Button label="Download PDF" icon="pi pi-file-pdf" size="small" outlined onClick={onDownloadPDF} />
                        </div>
                        {pdfPasswordHint && (
                            <div className="surface-100 border-round border-left-3 border-primary p-2 mb-2 flex align-items-start gap-2">
                                <i className="pi pi-lock text-primary text-sm mt-1" aria-hidden="true" />
                                <span className="text-600 text-sm line-height-3">{pdfPasswordHint}</span>
                            </div>
                        )}
                    </>
                )}

                {Object.keys(personal).length > 0 && (
                    <SmartSection title="PERSONAL" icon="pi-user" data={personal} highlightKeys={['full_name', 'mobile']} />
                )}

                {Object.keys(identity).length > 0 && (
                    <SmartSection title="IDENTITY" icon="pi-id-card" data={identity} />
                )}

                {phones.length > 0 && (
                    <div className="pt-3 pb-3 border-bottom-1 surface-border">
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-phone text-primary" aria-hidden="true" />
                            <span className="text-sm font-semibold text-900 uppercase">Phone Numbers ({phones.length})</span>
                        </div>
                        <Table rows={phones} cols={[
                            { field: 'number', header: 'Number' },
                            { field: 'type_code', header: 'Type', body: (r) => phoneType(r.type_code) },
                            { field: 'reported_date', header: 'Reported' },
                        ]} />
                    </div>
                )}

                {addresses.length > 0 && (
                    <div className="pt-3 pb-3 border-bottom-1 surface-border">
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-map-marker text-primary" aria-hidden="true" />
                            <span className="text-sm font-semibold text-900 uppercase">Addresses ({addresses.length})</span>
                        </div>
                        <Table rows={addresses} cols={[
                            { field: 'address', header: 'Address', style: { minWidth: '18rem' } },
                            { field: 'state', header: 'State' },
                            { field: 'postal', header: 'PIN' },
                            { field: 'type', header: 'Type', body: (r) => r.type || '—' },
                            { field: 'reported_date', header: 'Reported' },
                        ]} />
                    </div>
                )}

                {emails.length > 0 && (
                    <div className="pt-3 pb-3 border-bottom-1 surface-border">
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-envelope text-primary" aria-hidden="true" />
                            <span className="text-sm font-semibold text-900 uppercase">Email Addresses ({emails.length})</span>
                        </div>
                        <Table rows={emails} cols={[
                            { field: 'email_address', header: 'Email', style: { minWidth: '16rem' } },
                            { field: 'reported_date', header: 'Reported' },
                        ]} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PrefillByMobileOutputComponent;
